/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import Set from 'fierry/type/set';

import DirectionEnum from 'solis/type/hex/direction';


export default class Hex
{
  constructor (x, y, z)
  {
    if (x === undefined && y == undefined && z === undefined)
    {
      [ x, y, z ] = [ 0, 0, 0 ];
    }

    if (y === undefined && z === undefined)
    {
      [ x, y, z ] = this._initializeFromString(x);
    }

    if (z === undefined)
    {
      [ x, y, z ] = this._initializeFromOffset(x, y);
    }

    this.x = x;
    this.y = y;
    this.z = z;
  }


  _initializeFromString (value)
  {
    const [ row, col ] = value.split('.');

    return this._initializeFromOffset(parseInt(row), parseInt(col));
  }


  _initializeFromOffset (col, row)
  {
    const x = col - (row - (row & 1)) / 2;
    const z = row;
    const y = - x - z;

    return [ x, y, z ];
  }


  getX ()
  {
    return this.x;
  }


  getY ()
  {
    return this.y;
  }


  getZ ()
  {
    return this.z;
  }


  getCol ()
  {
    return this.x + (this.z - (this.z & 1)) / 2;
  }


  getRow ()
  {
    return this.z;
  }


  getDistance (rhs)
  {
    return (Math.abs(this.x - rhs.x) + Math.abs(this.y - rhs.y) + Math.abs(this.z - rhs.z)) / 2;
  }


  getDirection (other)
  {
    return this._getDirectionFromDelta(other.subtract(this));
  }


  _getDirectionFromDelta (delta)
  {
    if (delta.equals(new Hex( 1,  0, -1))) return DirectionEnum.TopRight;
    if (delta.equals(new Hex( 1, -1,  0))) return DirectionEnum.CenterRight;
    if (delta.equals(new Hex( 0, -1,  1))) return DirectionEnum.BottomRight;

    if (delta.equals(new Hex(-1,  0,  1))) return DirectionEnum.BottomLeft;
    if (delta.equals(new Hex(-1,  1,  0))) return DirectionEnum.CenterLeft;
    if (delta.equals(new Hex( 0,  1, -1))) return DirectionEnum.TopLeft;

    return DirectionEnum.None;
  }


  add (rhs)
  {
    const x = this.x + rhs.x;
    const y = this.y + rhs.y;
    const z = this.z + rhs.z;

    return new Hex(x, y, z);
  }


  subtract (rhs)
  {
    const x = this.x - rhs.x;
    const y = this.y - rhs.y;
    const z = this.z - rhs.z;

    return new Hex(x, y, z);
  }


  divide (value)
  {
    const x = this.x / value;
    const y = this.y / value;
    const z = this.z / value;

    return new Hex(x, y, z);
  }


  multiply (value)
  {
    const x = this.x * value;
    const y = this.y * value;
    const z = this.z * value;

    return new Hex(x, y, z);
  }


  negate ()
  {
    return new Hex(-this.x, -this.y, -this.z);
  }


  round ()
  {
    let roundX = Math.round(this.x)
    let roundY = Math.round(this.y)
    let roundZ = Math.round(this.z)

    const diffX = Math.abs(roundX - this.x);
    const diffY = Math.abs(roundY - this.y);
    const diffZ = Math.abs(roundZ - this.z);

    if (diffX > diffZ && diffX > diffY)
    {
      roundX = - roundY - roundZ;
    }

    else if (diffZ > diffY)
    {
      roundZ = - roundX - roundY;
    }
    else
    {
      roundY = - roundX - roundZ;
    }

    return new Hex(roundX, roundY, roundZ);
  }


  rotateLeft (count)
  {
    switch (this._normalizeRotation(count))
    {
      case 1:  return new Hex(- this.y, - this.z, - this.x);
      case 2:  return new Hex(  this.z,   this.x,   this.y);
      case 3:  return new Hex(- this.x, - this.y, - this.z);
      case 4:  return new Hex(  this.y,   this.z,   this.x);
      case 5:  return new Hex(- this.z, - this.x, - this.y);

      default: return this;
    }
  }


  rotateLeftWithCenter (center, count)
  {
    return this.subtract(center).rotateLeft(count).add(center);
  }


  rotateRight (count)
  {
    return this.rotateLeft(- count);
  }


  rotateRightWithCenter (center, count)
  {
    return this.rotateLeftWithCenter(center, - count);
  }


  _normalizeRotation (rotation)
  {
    rotation %= 6;

    if (rotation < 0)
    {
      rotation += 6;
    }

    return rotation;
  }


  transform (offset, rotate = 0)
  {
    return this.rotateRight(rotate).add(offset);
  }


  transformWithObject(object)
  {
    return this.transform(object.offset, object.rotation);
  }


  transformWithCenter (center, offset, rotate)
  {
    return this.rotateRightWithCenter(center, rotate).add(offset);
  }


  transformWithDirection(direction)
  {
    return this.add(this._getDeltaFromDirection(direction));
  }


  transformWithDirections(directions)
  {
    let result = this;

    for (const direction of directions)
    {
      result = result.add(result._getDeltaFromDirection(direction));
    }

    return result;
  }


  _getDeltaFromDirection (direction)
  {
    switch (direction)
    {
      case DirectionEnum.TopRight:    return new Hex(1, 0, -1);
      case DirectionEnum.CenterRight: return new Hex(1, -1, 0);
      case DirectionEnum.BottomRight: return new Hex(0, -1, 1);

      case DirectionEnum.BottomLeft:  return new Hex(-1, 0, 1);
      case DirectionEnum.CenterLeft:  return new Hex(-1, 1, 0);
      case DirectionEnum.TopLeft:     return new Hex(0, 1, -1);

      case DirectionEnum.None:        throw new Error('Unsupported DirectionEnum.None');
    }
  }


  inverseTransform (offset, rotate)
  {
    return this.subtract(offset).rotateLeft(rotate);
  }


  inverseTransformWithObject (object)
  {
    return this.inverseTransform(object.offset, object.rotation);
  }


  inverseTransformWithCenter (center, offset, rotate)
  {
    return this.subtract(offset).rotateLeftWithCenter(center, rotate);
  }


  getNeighbors (range = 1)
  {
    return this.getNeighborsWithFilter(null, range);
  }


  getNeighborsWithFilter (filterCallback, range = 1)
  {
    let neighbors = new Set();

    for (let x = -range; x <= range; ++x)
    {
      for (let y = Math.max(-range, -x - range); y <= Math.min(range, -x + range); ++y)
      {
        const neighbor = this.add(new Hex(x, y, - x - y));

        if (this.getDistance(neighbor) === range && (filterCallback == null || filterCallback(neighbor)))
        {
          neighbors.insert(neighbor);
        }
      }
    }

    return neighbors;
  }


  getNeighborsInOrder ()
  {
    const [ n0, n1, n2, n3, n4, n5 ] = this.getNeighbors(1);

    return [ n5, n4, n2, n0, n1, n3 ];
  }


  toString ()
  {
    return this.getCol() + '.' + this.getRow();
  }


  hashCode ()
  {
    return (this.x & 0xffff) << 16 | (this.z & 0xffff);
  }


  equals (rhs)
  {
    return this.x === rhs.x && this.y === rhs.y && this.z === rhs.z;
  }


  less (rhs)
  {
    if (this.x === rhs.x)
    {
      if (this.y === rhs.y)
      {
        return this.z < rhs.z;
      }

      return this.y < rhs.y;
    }

    return this.x < rhs.x;
  }


};
